/**
 * appy - A functional wrapper around Fetch API.
 *
 * @since 3.0.0
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API|Fetch Api}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch|Global fetch}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch|Using fetch}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Request|Request}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Response|Response}
 * @see {@link https://gcanti.github.io/fp-ts/ReaderTaskEither.html|ReaderTaskEither}
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as E from 'fp-ts/lib/Either';
import * as RTE from 'fp-ts/lib/ReaderTaskEither';
import * as TU from 'fp-ts/lib/Tuple';
import { pipe } from 'fp-ts/lib/pipeable';
/**
 * Makes a request using `fetch()` under the hood.
 *
 * The `data` in the returned `Resp` object is a `string` because the response's body can **always** be converted to text without errors (via [`text()`](https://developer.mozilla.org/en-US/docs/Web/API/Body/text) method).
 *
 * Example:
 * ```ts
 * import {request} from '@contactlab/appy';
 * import {fold} from 'fp-ts/lib/Either';
 *
 * // GET method as default like original fetch()
 * const posts = request('http://jsonplaceholder.typicode.com/posts');
 *
 * posts().then(
 *   fold(
 *     err => console.error(err),
 *     data => console.log(data)
 *   )
 * );
 * ```
 *
 * @since 3.0.0
 */
export const request = input => () => {
    const reqInput = normalizeReqInput(input);
    return fetch(...reqInput)
        .then((response) => __awaiter(this, void 0, void 0, function* () {
        if (!response.ok) {
            throw toResponseError(new Error(`Request responded with status code ${response.status}`), response);
        }
        const data = yield response.text();
        return E.right({ response, data });
    }), e => {
        throw toRequestError(e, reqInput);
    })
        .catch(e => E.left(e));
};
/**
 * Makes a request with the `method` set to `GET`.
 *
 * @since 3.0.0
 */
export const get = pipe(request, RTE.local(setMethod('GET')));
/**
 * Makes a request with the `method` set to `POST`.
 *
 * @since 3.0.0
 */
export const post = pipe(request, RTE.local(setMethod('POST')));
/**
 * Makes a request with the `method` set to `PUT`.
 *
 * @since 3.0.0
 */
export const put = pipe(request, RTE.local(setMethod('PUT')));
/**
 * Makes a request with the `method` set to `PATCH`.
 *
 * @since 3.0.0
 */
export const patch = pipe(request, RTE.local(setMethod('PATCH')));
/**
 * Makes a request with the `method` set to `DELETE`.
 *
 * @since 3.0.0
 */
export const del = pipe(request, RTE.local(setMethod('DELETE')));
/**
 * Creates a `RequestError` object.
 *
 * @since 3.0.0
 */
export function toRequestError(error, input) {
    return { type: 'RequestError', error, input };
}
/**
 * Creates a `ResponseError` object.
 *
 * @since 3.0.0
 */
export function toResponseError(error, response) {
    return { type: 'ResponseError', response, error };
}
/**
 * Normalizes the input of a `Req` to a `RequestInfoInit` tuple even when only a single `RequestInfo` is provided.
 *
 * @since 3.0.0
 */
export function normalizeReqInput(input) {
    return Array.isArray(input) ? input : [input, {}];
}
function setMethod(method) {
    return input => pipe(normalizeReqInput(input), TU.mapLeft(init => (Object.assign({}, init, { method }))));
}
//# sourceMappingURL=index.js.map